import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api, ApiError } from '../lib/api.js';

/* ============================================================================
   /reset-password?token=...
   ----------------------------------------------------------------------------
   The token arrives in the query string because it arrives in an email link.
   It is single use and expires in 30 minutes; the API stores only its hash.
   ========================================================================== */

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();

    // Checked here rather than by the API: a confirmation field is a typing
    // aid, not a rule about what a valid password is.
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }

    setBusy(true);
    setError('');
    try {
      await api.post('/api/auth/reset', { token, password });
      setDone(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not reset the password.');
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <div className="centre">
        <div className="centre__box card stack">
          <h1>That reset link is incomplete</h1>
          <p className="muted">Open the whole link from the email, or ask for a new one.</p>
          <Link to="/forgot-password" className="btn">
            Send me a new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="centre">
      <div className="centre__box card stack">
        <h1>New password</h1>

        {done ? (
          <>
            <p className="alert alert--ok" role="status">
              Your password has changed. You can log in now.
            </p>
            <Link to="/login" className="btn">
              Login
            </Link>
          </>
        ) : (
          <form className="stack" onSubmit={(e) => void onSubmit(e)} noValidate>
            {error ? (
              <p className="alert" role="alert">
                {error}
              </p>
            ) : null}

            <div className="field">
              <label htmlFor="password">New password</label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                minLength={12}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <span className="muted">At least 12 characters.</span>
            </div>

            <div className="field">
              <label htmlFor="confirm">Type it again</label>
              <input
                id="confirm"
                name="confirm"
                type="password"
                autoComplete="new-password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>

            <button className="btn" type="submit" disabled={busy}>
              {busy ? 'Saving…' : 'Change password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
